import { useState, useCallback } from 'react'

import { useGetEmployerRefereeQuery } from '@/lib/apollo'
import type { EmployerReferee } from '@/lib/apollo/query/get-employer-referee'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'

import { graphQLQuery } from './hooks'

export type FetchSingleApplication = (
  customerId: string,
  subjectId: string,
) => Promise<void>

const useEmployerReferee = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [employerReferee, setEmployerReferee] = useState<EmployerReferee | undefined>(
    undefined,
  )
  const [application, setApplication] = useState<Application | undefined>(undefined)

  const [fetchEmployerReferee] = useGetEmployerRefereeQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  const fetchSingleApplication = useCallback<FetchSingleApplication>(
    async (customerId: string, subjectId: string) => {
      setLoading(true)
      setApplication(undefined)
      setEmployerReferee(undefined)
      setError(undefined)

      try {
        const { data: employerRefereeData } = await fetchEmployerReferee({
          variables: {
            customerId,
            subjectId,
          },
        })

        if (!employerRefereeData) {
          throw Error('No subject was retrieved')
        }

        setEmployerReferee(employerRefereeData?.getEmployerReferee)

        const getSingleApplicationResponse = await graphQLQuery({
          query: QUERIES.getSingleApplication,
          variables: {
            customerId,
            applicationId: employerRefereeData?.getEmployerReferee.applicationId,
            subjectId,
          },
          endpoint: `${import.meta.env.VITE_REPORTS_GRAPHQL}`,
        })

        const data = await getSingleApplicationResponse.json()

        if (!data || !data.data) {
          throw Error('Get single application returned undefined')
        }

        setApplication(data.data.getDashboardApplication)
      } catch (e) {
        console.error(e)
        setError('Failed to retrieve applicant')
      } finally {
        setLoading(false)
      }
    },
    [fetchEmployerReferee, setLoading, setError, setApplication],
  )

  return {
    loading,
    error,
    employerReferee,
    application,
    fetchSingleApplication,
  }
}

export default useEmployerReferee
